import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import { selectAISentimentLabel } from '../../redux/features/ai/ai.selector';
import { selectAIIndicator } from '../../selectors/restaurant';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import Colors from '../../utils/color';

/**
 * SENTIMENT (HITL 3.0 Pulse tile).
 *
 * The latest customer sentiment from `ai_session_state` (`data.sentiment.label`).
 * Rendered as a fixed vitals-row tile (NOT a chip) because it's a persistent
 * state the agent must be able to read at a glance — the colour carries the
 * meaning, so there is no numeral or progress bar:
 *   positive → green, neutral → grey, negative/frustrated → red.
 *
 * Shown during an active session (valid session id) AND only when AI is enabled
 * (supervised or unsupervised) — sentiment is an AI signal, so it is hidden in
 * pure HITL mode. While shown it is a muted "—" placeholder until a label
 * arrives, and back to that on each new order (the ai slice clears the label).
 * Unknown labels render with neutral styling (the raw label still shows), so a
 * new backend value never disappears. Gated overall by hitl_3_0.
 */

const PLACEHOLDER = '—';

type Tone = 'positive' | 'neutral' | 'negative' | 'empty';

// Label → tone. Neutral is also the fallback for any unrecognised label, so a
// value we don't have a mapping for still shows (just without a green/red cue).
const POSITIVE_LABELS = [
  'positive',
  'happy',
  'satisfied',
  'pleased',
  'good',
  'delighted',
];
const NEGATIVE_LABELS = [
  'negative',
  'frustrated',
  'angry',
  'upset',
  'annoyed',
  'dissatisfied',
  'bad',
];

const toneForLabel = (label: string): Tone => {
  const normalized = label.trim().toLowerCase();
  if (!normalized) return 'empty';
  if (POSITIVE_LABELS.includes(normalized)) return 'positive';
  if (NEGATIVE_LABELS.includes(normalized)) return 'negative';
  return 'neutral';
};

const COLOR_BY_TONE: Record<Tone, string> = {
  positive: Colors.emerald, // #30d158 — matches the meter tiles' green
  neutral: '#616872', // calm slate — distinct from the lighter placeholder
  negative: Colors.bittersweet, // #FC7373 — matches the meter tiles' red
  empty: '#a7adb6', // light muted grey — "no reading yet"
};

// Capitalize the first letter only: "neutral" → "Neutral".
const sentenceCase = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const useStyles = makeStyles((theme: Theme) => ({
  // Mirrors PulseMeterTile's tile/value/label typography so it sits flush with
  // the numeric tiles in the metric row.
  tile: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.375),
    width: 'fit-content',
  },
  value: {
    fontSize: '1.02rem',
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '-0.01em',
  },
  label: {
    fontSize: '0.6rem',
    color: '#79808a',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
}));

const SentimentTile = () => {
  const classes = useStyles();
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const sentimentLabel = useAppSelector(selectAISentimentLabel);
  const aiEnabled = useAppSelector(selectAIIndicator);
  const sessionId = useAppSelector(selectSessionId);

  if (!isHitl3 || !sessionId || !aiEnabled) {
    return null;
  }

  const tone = toneForLabel(sentimentLabel);
  const color = COLOR_BY_TONE[tone];
  const text = tone === 'empty' ? PLACEHOLDER : sentenceCase(sentimentLabel);

  return (
    <div className={classes.tile} data-testid="sentiment-tile" data-tone={tone}>
      <div className={classes.value} style={{ color }}>
        {text}
      </div>
      <div className={classes.label}>Sentiment</div>
    </div>
  );
};

export default SentimentTile;
